import React from 'react';
import {
  RepossessionRecoveryCostType,
  RepossessionStatus,
  RepossessionStatusHistoryApi,
} from '@/types/common';

interface StatusTimelineProps {
  statusHistory: RepossessionStatusHistoryApi[];
}

const formatTimelineDateTime = (value: string) => {
  if (!value) {
    return { date: '-', time: '' };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const parts = value.split(' ');
    return {
      date: parts.slice(0, 3).join(' ') || value,
      time: parts.slice(3).join(' '),
    };
  }

  const date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsed);

  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);

  return { date, time };
};

const getStatusPillClasses = (status?: RepossessionStatus | string) => {
  switch (status) {
    case RepossessionStatus.FOUND:
      return 'bg-green-100 text-green-700';
    case RepossessionStatus.PENDING_APPROVAL:
      return 'bg-[#EFEAF7] text-[#5E2AAC]';
    case RepossessionStatus.IN_PROGRESS:
      return 'bg-yellow-100 text-yellow-700';
    case RepossessionStatus.OPEN:
      return 'bg-blue-100 text-blue-700';
    case RepossessionStatus.POTENTIAL_LOSS:
      return 'bg-red-100 text-red-700';
    case RepossessionStatus.WRITTEN_OFF:
      return 'bg-[#FFF3EB] text-[#FA7319]';
    case RepossessionStatus.CLOSED:
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatStatusLabel = (status?: RepossessionStatus | string) => {
  if (!status) return 'UNKNOWN';
  return status.toString().replace(/_/g, ' ');
};

const formatMetaLabel = (value?: string) => {
  if (!value) return '';
  return value.toString().replace(/_/g, ' ');
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '';
  return `Rp. ${value.toLocaleString('id-ID')}`;
};

const getRecoveryCostTypeLabel = (
  value?: RepossessionRecoveryCostType | string,
  otherLabel?: string
) => {
  if (!value) return 'recovery cost';

  switch (value) {
    case RepossessionRecoveryCostType.TOWING_FEE:
      return 'TOWING FEE';
    case RepossessionRecoveryCostType.LEGAL_FEE:
      return 'LEGAL FEE';
    case RepossessionRecoveryCostType.STORAGE_FEE:
      return 'STORAGE FEE';
    case RepossessionRecoveryCostType.VEHICLE_DAMAGE:
      return 'VEHICLE DAMAGE';
    case RepossessionRecoveryCostType.OTHER: {
      const trimmed = otherLabel?.trim();
      return trimmed ? `OTHER - ${trimmed}` : 'OTHER';
    }
    default:
      return formatMetaLabel(value);
  }
};

const getActionText = (entry: RepossessionStatusHistoryApi) => {
  const decision = entry.metadata?.decision;
  const escalationTrigger = entry.metadata?.escalationTrigger;

  switch (entry.toStatus) {
    case RepossessionStatus.OPEN:
      return 'Case created - vehicle missing';
    case RepossessionStatus.IN_PROGRESS:
      if (decision === 'REJECT_CONTINUE_SEARCH') return 'Rejected';
      if (decision === 'APPROVE_CONTINUE_SEARCH') return 'Approve Recovery';
      return 'Started vehicle search and recovery process';
    case RepossessionStatus.PENDING_APPROVAL:
      return 'Waiting for Manager’s Approval';
    case RepossessionStatus.POTENTIAL_LOSS:
      if (decision === 'REJECT_CONTINUE_SEARCH') return 'Rejected';
      if (escalationTrigger === 'MANUAL') return 'Manually Escalated';
      if (escalationTrigger === 'AUTOMATIC') {
        return 'Vehicle not found after 48 hours - automatically escalated';
      }
      if (entry.reason) return 'Manually Escalated';
      return 'Vehicle not found after 48 hours - automatically escalated';
    case RepossessionStatus.FOUND:
      if (decision === 'APPROVE_RECOVERY') return 'Approve Recovery';
      return '';
    case RepossessionStatus.WRITTEN_OFF:
      if (decision === 'APPROVE_TOTAL_LOSS') return 'Approve Total Loss';
      return '';
    case RepossessionStatus.CLOSED:
    default:
      return '';
  }
};

const StatusTimeline: React.FC<StatusTimelineProps> = ({ statusHistory }) => {
  return (
    <div className="w-full max-w-full overflow-hidden sticky top-5 rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="px-5 py-4">
        <div className="text-base font-semibold text-gray-900">
          Status Timeline
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50 px-5 py-2">
        <div className="text-xs font-medium tracking-wide text-gray-400">
          Repo Progress
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="space-y-4">
          {statusHistory.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
              No status history available yet.
            </div>
          )}
          {statusHistory.map((entry, index) => {
            const { date, time } = formatTimelineDateTime(
              entry.createdAt || ''
            );
            const metadata = entry.metadata;
            const isCurrent = index === 0;
            const detailTextClass = isCurrent
              ? 'text-black mt-0.5'
              : 'text-gray-500';
            const detailMutedClass = isCurrent ? 'text-black' : 'text-gray-400';
            const timeTextClass = isCurrent ? 'text-black' : 'text-gray-500';
            const statusPill = getStatusPillClasses(entry.toStatus);
            const statusLabel = formatStatusLabel(entry.toStatus);
            const actionText = getActionText(entry);
            const noteText = entry.metadata?.reason;
            const showWarningIcon =
              metadata?.decision === 'REJECT_CONTINUE_SEARCH';

            return (
              <div key={entry.ID} className="flex items-start gap-3">
                <div className="flex flex-col items-center self-stretch">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      isCurrent
                        ? 'border-purple-400 text-purple-600'
                        : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {isCurrent ? (
                      <div className="h-2.5 w-2.5 rounded-full bg-purple-600" />
                    ) : (
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  {index !== statusHistory.length - 1 && (
                    <div className="w-px flex-1 bg-gray-200" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="text-sm font-medium text-gray-900">
                          {date}
                        </div>
                      </div>
                      <span
                        className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusPill}`}
                      >
                        {statusLabel}
                        {actionText && (
                          <span className="group relative flex items-center">
                            <i
                              className={
                                showWarningIcon
                                  ? 'ri-alert-line text-[12px] text-amber-500'
                                  : 'ri-information-line text-[10px]'
                              }
                            />
                            <span className="pointer-events-none absolute left-1/2 top-[-38px] hidden w-max max-w-[220px] -translate-x-1/2 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-500 shadow-sm group-hover:block">
                              {actionText}
                            </span>
                          </span>
                        )}
                      </span>
                    </div>
                    <div className={`text-xs ${timeTextClass}`}>{time}</div>
                  </div>

                  <div className={`mt-2 space-y-1 text-xs ${detailTextClass}`}>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </span>
                      <span className="flex-1 break-all overflow-hidden">
                        {entry.createdBy}
                      </span>
                    </div>

                    {noteText && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </span>
                        <span
                          className={`mt-1 flex-1 break-all overflow-hidden ${detailTextClass}`}
                        >
                          {noteText}
                        </span>
                      </div>
                    )}

                    {metadata?.recoveryCost !== undefined &&
                      metadata?.recoveryCost !== null &&
                      metadata?.recoveryCost > 0 && (
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                            <i className="ri-cash-line text-xs text-gray-400" />
                          </span>
                          <span className={`mt-1 ${detailTextClass}`}>
                            {formatCurrency(metadata.recoveryCost)} for{' '}
                            {getRecoveryCostTypeLabel(
                              metadata.recoveryCostType,
                              metadata.recoveryCostTypeOther
                            )}
                          </span>
                        </div>
                      )}

                    {metadata?.recoveryNotes && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <i className="ri-file-text-line text-xs text-gray-400" />
                        </span>
                        <div>
                          <div
                            className={`mt-1 break-all overflow-hidden ${detailTextClass}`}
                          >
                            Notes: {formatMetaLabel(metadata.recoveryNotes)}
                          </div>
                        </div>
                      </div>
                    )}

                    {metadata?.decisionNotes && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </span>
                        <span
                          className={`mt-1 flex-1 break-all overflow-hidden ${detailTextClass}`}
                        >
                          Notes: {formatMetaLabel(metadata.decisionNotes)}
                        </span>
                      </div>
                    )}

                    {metadata?.opsRecommendation && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <i className="ri-lightbulb-line text-xs text-gray-400" />
                        </span>
                        <span
                          className={`mt-1 flex-1 break-all overflow-hidden ${detailTextClass}`}
                        >
                          Recommendation:{' '}
                          {formatMetaLabel(metadata.opsRecommendation)}
                        </span>
                      </div>
                    )}

                    {metadata?.estimatedCost !== undefined &&
                      metadata?.estimatedCost !== null && (
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                            <i className="ri-money-dollar-circle-line text-xs text-gray-400" />
                          </span>
                          <span className={detailTextClass}>
                            Estimated Cost:{' '}
                            {formatCurrency(metadata.estimatedCost)}
                          </span>
                        </div>
                      )}

                    {metadata?.searchEfforts && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <i className="ri-search-eye-line text-xs text-gray-400" />
                        </span>
                        <div>
                          <div className={detailMutedClass}>
                            Search Efforts Made:
                          </div>
                          <div
                            className={`break-all overflow-hidden ${detailTextClass}`}
                          >
                            {metadata.searchEfforts}
                          </div>
                        </div>
                      </div>
                    )}

                    {metadata?.escalationReason && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <i className="ri-error-warning-line text-xs text-gray-400" />
                        </span>
                        <div>
                          <div className={detailMutedClass}>
                            Why Escalating Now:
                          </div>
                          <div
                            className={`break-all overflow-hidden ${detailTextClass}`}
                          >
                            {metadata.escalationReason}
                          </div>
                        </div>
                      </div>
                    )}

                    {metadata?.lastKnownInfo && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <i className="ri-map-pin-line text-xs text-gray-400" />
                        </span>
                        <div>
                          <div className={detailMutedClass}>
                            Last Known Information:
                          </div>
                          <div
                            className={`break-all overflow-hidden ${detailTextClass}`}
                          >
                            {metadata.lastKnownInfo}
                          </div>
                        </div>
                      </div>
                    )}

                    {metadata?.settlementAmount !== undefined &&
                      metadata?.settlementAmount !== null && (
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                            <i className="ri-money-dollar-circle-line text-xs text-gray-400" />
                          </span>
                          <span className={detailTextClass}>
                            {formatCurrency(metadata.settlementAmount)} for{' '}
                            {formatMetaLabel(
                              metadata.settlementType || 'settlement'
                            )}
                          </span>
                        </div>
                      )}

                    {metadata?.settlementNotes && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <i className="ri-file-text-line text-xs text-gray-400" />
                        </span>
                        <div>
                          <div className={detailMutedClass}>
                            Settlement Notes:
                          </div>
                          <div
                            className={`break-all overflow-hidden ${detailTextClass}`}
                          >
                            {metadata.settlementNotes}
                          </div>
                        </div>
                      </div>
                    )}

                    {metadata?.settlementDocumentURL && (
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                          <i className="ri-attachment-2 text-xs text-gray-400" />
                        </span>
                        <div>
                          <div className={detailMutedClass}>
                            Settlement Document:
                          </div>
                          <a
                            href={metadata.settlementDocumentURL}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline"
                          >
                            View document
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StatusTimeline;
